import { useQuery } from '@apollo/client';
import {
  Loader,
  ALL_SHARD_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  ActionGroup,
  getParsedCrudData
} from '@nile/components';
import { AccountClient, GET_FIELD } from '@nile/graphql/src';
import {
  Badge,
  Button,
  Heading,
  Page,
  ResourceItem,
  TextField
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, isEmpty, map, pick, sortBy } from 'lodash';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import nanoid from 'nanoid';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ICON_MAP = {
  SERVICE_REQUEST: { icon: 'Cart', color: 'info' },
  INCIDENT: { icon: 'InlineError', color: 'critical' }
};

const EditMode = ({ item, onSave, onCancel, icon, disableSort }) => {
  const [value, setValue] = useState(item?.value);

  const [data, setData] = useState(item);

  const onChange = (val, key) => {
    setValue(val);
    const updatedItem = cloneDeep(data);
    if (updatedItem?.[key]) {
      updatedItem[key] = val || null;
    }
    setData(updatedItem);
  };

  const save = () => {
    if (onSave) {
      onSave(data);
    }
  };

  const cancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="Ticket-Status-Form flex actr g10 mt-5 mb-5 br-5 pr10 pl10 wm-800 m0imp height50 ">
      {!disableSort && (
        <div>
          <SuperSortable.DragHandle />
        </div>
      )}

      <div>
        <Badge
          icon={<SVGIcon name={icon?.name} size="sm" />}
          radial
          status={icon?.status}
          size="small"
        />
      </div>

      <div className="w100">
        <TextField
          noborder
          placeholder="Edit ticket Type"
          onChange={(val) => {
            onChange(val, 'value');
          }}
          value={value}
        />
      </div>
      <div className="flex g5">
        <Button
          onClick={save}
          // size="slim"
          icon={<SVGIcon name="Check" />}
        />

        <Button
          onClick={cancel}
          // size="slim"
          icon={<SVGIcon name="RejectedCircle" />}
        />
      </div>
    </div>
  );
};
function TicketType(props) {
  const flags = useFlags();
  const getIcon = (property) => {
    const name = ICON_MAP?.[property]?.icon || 'Cube';
    const status = ICON_MAP?.[property]?.color || null;

    return { name, status };
  };
  const { primaryAction, isEditAuthorised, disableSort } = props;

  const [options, setOptions] = useState(null);
  const [newType, setNewType] = useState(null);

  const [editItem, setEditItem] = useState(null);

  const onEdit = (item) => {
    setEditItem({ item, original: item });
  };

  const addOptions = () => {
    if (newType) {
      let updatedOptions = options ? cloneDeep(options) : [];
      const order = updatedOptions?.length + 1;
      const typeToAdd = {
        id: nanoid(),
        order: order,
        value: newType,
        details: {
          deletable: true
        }
      };
      updatedOptions = [...updatedOptions, typeToAdd];
      setOptions(updatedOptions);
      setNewType(null);
    }
  };

  const deleteItem = (item) => {
    const allOptions = cloneDeep(options);
    const updatedOptions = [];
    allOptions.forEach((el, index) => {
      if (el?.id !== item?.id) {
        updatedOptions.push({
          ...el,
          order: index + 1
        });
      }
    });
    setOptions(updatedOptions);
  };

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.TICKET_TYPE
      }
    },
    onCompleted({ getField }) {
      if (getField?.options || getField?.fieldOptions) {
        const sortedData = sortBy(
          getField?.options || getField?.fieldOptions,
          'order'
        );
        setOptions(sortedData);
      }
    }
  });

  const onEditCancel = () => {
    setEditItem(null);
  };

  const onEditSave = (updatedItem) => {
    let updatedOptions = cloneDeep(options);
    updatedOptions = updatedOptions.map((el) => {
      if (el?.id === updatedItem?.id) {
        return updatedItem;
      }
      return el;
    });
    setOptions(updatedOptions);
    setEditItem(null);
  };

  const onSave = () => {
    const base = data?.getField?.options || data?.getField?.fieldOptions;
    const parsedData = getParsedCrudData(base, options);
    const { addedItems, deletedItems, updatedItems } = parsedData;

    const fieldOptionsInput = {
      ...(addedItems?.length && {
        added: addedItems.map(({ value, order, details }) => ({
          value,
          order,
          details
        }))
      }),
      ...(deletedItems?.length && {
        deleted: deletedItems
      }),
      ...(updatedItems?.length && {
        updated: updatedItems,
        updateOptions: updatedItems
      })
    };

    let variables = { id: data?.getField?.id };

    if (!isEmpty(fieldOptionsInput)) {
      if (flags?.dependentField) {
        const optionsInput = {
          ...(addedItems?.length && {
            addOptions: addedItems.map(({ value, order }) => ({
              value,
              order
            }))
          }),
          ...(deletedItems?.length && {
            deleteOptions: deletedItems.map(({ value, order, id }) => ({
              id
            }))
          }),
          ...(updatedItems?.length && {
            updateOptions: updatedItems.map(({ value, order, id }) => ({
              id,
              value,
              order
            }))
          })
        };
        variables = {
          ...variables,
          ...optionsInput
        };
      } else {
        variables = {
          fieldOptionsInput: JSON.stringify(fieldOptionsInput)
        };
      }
      props.primaryAction(variables);
    } else {
      props?.secondaryAction();
    }
  };

  if (error) return <div>error occured </div>;

  return (
    <div className="full-height full-width">
      <div className="full-height full-width">
        <Page
          fullWidth
          variant="primary"
          contentHeight="140px"
          title={
            <Heading element="h1">
              <FormattedMessage id="admin.defaultField.ticketType" />
            </Heading>
          }
          subtitle={
            <FormattedMessage id="admin.defaultField.ticketType.description" />
          }
          // primaryAction={
          //   <div onClick={props.secondaryAction} className="pointer">
          //     <SVGIcon name="Close" />
          //   </div>
          // }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={onSave}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          {loading && !options ? (
            <>
              <Loader />
            </>
          ) : (
            <>
              {isEditAuthorised && (
                <div className="Ticket-Status-Form flex actr g5 mt-0 br-5 pr10 pl10 mb-20 wm-800 height40">
                  <div className="w100">
                    <TextField
                      noborder
                      placeholder="Add ticket Type"
                      onChange={(val) => {
                        setNewType(val);
                      }}
                      value={newType}
                    />
                  </div>
                  <div>
                    <Button plain onClick={addOptions}>
                      Add
                    </Button>
                  </div>
                </div>
              )}

              <SuperSortable
                className="sort-wrapper"
                list={options || []}
                onDragEnd={(reorderedData) => {
                  const updatedDataWithOrder = reorderedData.map(
                    (field, i) => ({
                      ...field,
                      order: i + 1
                    })
                  );
                  setOptions(updatedDataWithOrder);
                }}
                animation={200}
              >
                {options &&
                  options.map((el) => {
                    const property = el?.details?.property;
                    const icon = getIcon(property);
                    const isDeletable =
                      (el?.deletable || el?.details?.deletable) && !property;

                    if (el?.id === editItem?.item?.id) {
                      return (
                        <React.Fragment key={el?.id}>
                          <EditMode
                            item={el}
                            onSave={onEditSave}
                            onCancel={onEditCancel}
                            icon={icon}
                            disableSort={disableSort}
                          />
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={el?.id}>
                        <div className="wm-800 hiddenContainer br-5">
                          <ResourceItem>
                            <div className="flex fx-sb actr">
                              <div className="flex g10">
                                {isEditAuthorised && !disableSort && (
                                  <SuperSortable.DragHandle />
                                )}
                                <div>
                                  <Badge
                                    icon={
                                      <SVGIcon name={icon?.name} size="sm" />
                                    }
                                    radial
                                    status={icon?.status}
                                    size="small"
                                  />
                                </div>
                                <div>
                                  <Heading element="h3" weight="SemiBold">
                                    {el?.value}
                                  </Heading>
                                </div>
                              </div>

                              {isEditAuthorised && (
                                <div className="hiddenActions">
                                  <div className="flex g5">
                                    <Button
                                      icon={<SVGIcon name="Edit" />}
                                      plain
                                      onClick={() => onEdit(el)}
                                    />

                                    {isDeletable && (
                                      <Button
                                        icon={<SVGIcon name="Delete" />}
                                        plain
                                        onClick={() => deleteItem(el)}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </ResourceItem>
                        </div>
                      </React.Fragment>
                    );
                  })}
              </SuperSortable>
            </>
          )}
        </Page>
      </div>
    </div>
  );
}

export default TicketType;
